import {
  query,
  Query,
  CollectionReference,
  where,
  or,
  and,
  orderBy,
  limit,
  startAfter,
  Timestamp,
  QueryConstraint,
  QueryNonFilterConstraint,
  QueryFilterConstraint,
  QueryCompositeFilterConstraint,
  DocumentSnapshot,
  FieldPath
} from 'firebase/firestore';

export interface QueryOptions {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  search?: string;
}

export interface DateRangeFilter {
  field: string;
  dateFrom?: Date;
  dateTo?: Date;
}

export interface FilterCondition {
  field: string | FieldPath;
  operator: '==' | '!=' | '<' | '<=' | '>' | '>=' | 'in' | 'not-in' | 'array-contains' | 'array-contains-any' | 'or-equals';
  value: any;
}

export class FirestoreQueryBuilder {
  private filterConstraints: QueryFilterConstraint[] = [];
  private nonFilterConstraints: QueryNonFilterConstraint[] = [];
  private hasComposite = false;

  constructor(private collection: CollectionReference) {}

  withFilter(condition: FilterCondition): FirestoreQueryBuilder {
    if (condition.operator === 'or-equals') {
      return this.withOrEquals(condition.field, condition.value as any[]);
    }
    this.filterConstraints.push(where(condition.field, condition.operator as any, condition.value));
    return this;
  }

  withOrEquals(field: string | FieldPath, values: any[]): FirestoreQueryBuilder {
    if (values.length === 0) return this;
    if (values.length === 1) {
      this.filterConstraints.push(where(field, '==', values[0]));
    } else {
      this.filterConstraints.push(or(...values.map(v => where(field, '==', v))));
      this.hasComposite = true;
    }
    return this;
  }

  withFilters(conditions: FilterCondition[]): FirestoreQueryBuilder {
    conditions.forEach(condition => this.withFilter(condition));
    return this;
  }

  withDateRange(dateRange: DateRangeFilter): FirestoreQueryBuilder {
    if (dateRange.dateFrom) {
      this.filterConstraints.push(where(dateRange.field, '>=', Timestamp.fromDate(dateRange.dateFrom)));
    }
    if (dateRange.dateTo) {
      this.filterConstraints.push(where(dateRange.field, '<=', Timestamp.fromDate(dateRange.dateTo)));
    }
    return this;
  }

  withSorting(sortBy: string, sortOrder: 'asc' | 'desc' = 'asc', fieldMapper?: (field: string) => string): FirestoreQueryBuilder {
    const mappedField = fieldMapper ? fieldMapper(sortBy) : sortBy;
    this.nonFilterConstraints.push(orderBy(mappedField, sortOrder));
    return this;
  }

  withPagination(options: { pageSize: number; lastDoc?: DocumentSnapshot }): FirestoreQueryBuilder {
    this.nonFilterConstraints.push(limit(options.pageSize));
    if (options.lastDoc) {
      this.nonFilterConstraints.push(startAfter(options.lastDoc));
    }
    return this;
  }

  private buildFromConstraints(
    filterConstraints: QueryFilterConstraint[],
    nonFilterConstraints: QueryNonFilterConstraint[]
  ): Query {
    if (filterConstraints.length === 0 && nonFilterConstraints.length === 0) {
      return this.collection;
    }
    if (filterConstraints.length === 0) {
      return query(this.collection, ...nonFilterConstraints);
    }
    if (!this.hasComposite) {
      // All filters are simple field filters — assignable to QueryConstraint
      return query(
        this.collection,
        ...(filterConstraints as unknown as QueryConstraint[]),
        ...nonFilterConstraints
      );
    }
    // Has composite filters (or/and): wrap all filters in and() and use composite overload
    const compositeFilter: QueryCompositeFilterConstraint =
      filterConstraints.length === 1
        ? (filterConstraints[0] as QueryCompositeFilterConstraint)
        : and(...filterConstraints);
    return query(this.collection, compositeFilter, ...nonFilterConstraints);
  }

  build(): Query {
    return this.buildFromConstraints(this.filterConstraints, this.nonFilterConstraints);
  }

  buildCount(): Query {
    const countNonFilters = this.nonFilterConstraints.filter(c => {
      const cs = c.toString();
      return !cs.includes('limit') && !cs.includes('startAfter');
    });
    return this.buildFromConstraints(this.filterConstraints, countNonFilters);
  }

  reset(): FirestoreQueryBuilder {
    this.filterConstraints = [];
    this.nonFilterConstraints = [];
    this.hasComposite = false;
    return this;
  }

  static create(collection: CollectionReference): FirestoreQueryBuilder {
    return new FirestoreQueryBuilder(collection);
  }
}
